import { Building2, Check, Edit3, Loader2, MapPin, Plus, Tags, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../../api/client";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { useToast } from "../../../context/ToastContext";
import type { AdminVenue, EventCategory } from "../../../types/api";

type CategoryDraft = {
  name: string;
  description: string;
};

type VenueDraft = {
  name: string;
  city: string;
  address: string;
  capacity: string;
  notes: string;
};

const emptyCategory: CategoryDraft = {
  name: "",
  description: "",
};

const emptyVenue: VenueDraft = {
  name: "",
  city: "",
  address: "",
  capacity: "",
  notes: "",
};

function categoryDraft(category: EventCategory): CategoryDraft {
  return {
    name: category.name,
    description: category.description ?? "",
  };
}

function venueDraft(venue: AdminVenue): VenueDraft {
  return {
    name: venue.name,
    city: venue.city,
    address: venue.address,
    capacity: venue.capacity ? String(venue.capacity) : "",
    notes: venue.notes ?? "",
  };
}

function parseCapacity(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return 0;
  const capacity = Number(trimmed);
  if (!Number.isFinite(capacity) || capacity < 0) {
    throw new Error("Capacity must be 0 or more");
  }
  return Math.floor(capacity);
}

function activeBadge(active: boolean) {
  return active ? "ego-badge ego-badge-success" : "ego-badge ego-badge-neutral";
}

export default function AdminCatalogPage() {
  const { admin, token } = useAdminAuth();
  const { showToast } = useToast();
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [venues, setVenues] = useState<AdminVenue[]>([]);
  const [categoryForm, setCategoryForm] = useState<CategoryDraft>(emptyCategory);
  const [venueForm, setVenueForm] = useState<VenueDraft>(emptyVenue);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingVenueId, setEditingVenueId] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<CategoryDraft>(emptyCategory);
  const [editingVenue, setEditingVenue] = useState<VenueDraft>(emptyVenue);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canManage = admin?.role === "super-admin" || admin?.effectivePermissions?.includes("catalog:manage");

  const loadCatalog = async () => {
    if (!token) return;
    setError(null);
    try {
      const response = await adminApi.listCatalog(token);
      setCategories(response.categories);
      setVenues(response.venues);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Could not load catalog";
      setError(message);
      showToast(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadCatalog();
  }, [token]);

  const stats = useMemo(
    () => ({
      activeCategories: categories.filter((category) => category.active).length,
      inactiveCategories: categories.filter((category) => !category.active).length,
      activeVenues: venues.filter((venue) => venue.active).length,
      inactiveVenues: venues.filter((venue) => !venue.active).length,
    }),
    [categories, venues]
  );

  const createCategory = async () => {
    if (!token) return;
    if (!categoryForm.name.trim()) {
      showToast("Category name is required", "error");
      return;
    }

    setIsSaving(true);
    try {
      const response = await adminApi.createCategory(token, {
        name: categoryForm.name,
        description: categoryForm.description,
      });
      setCategories((current) => [...current, response.category].sort((a, b) => a.name.localeCompare(b.name)));
      setCategoryForm(emptyCategory);
      showToast("Category added", "success");
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Could not add category", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const saveCategory = async (category: EventCategory) => {
    if (!token) return;
    if (!editingCategory.name.trim()) {
      showToast("Category name is required", "error");
      return;
    }

    setIsSaving(true);
    try {
      const response = await adminApi.updateCategory(token, category.id, {
        name: editingCategory.name,
        description: editingCategory.description,
      });
      setCategories((current) =>
        current.map((entry) => (entry.id === category.id ? response.category : entry))
      );
      setEditingCategoryId(null);
      showToast("Category updated", "success");
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Could not update category", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCategory = async (category: EventCategory) => {
    if (!token) return;
    try {
      const response = await adminApi.updateCategory(token, category.id, { active: !category.active });
      setCategories((current) =>
        current.map((entry) => (entry.id === category.id ? response.category : entry))
      );
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Could not update category", "error");
    }
  };

  const deleteCategory = async (category: EventCategory) => {
    if (!token) return;
    if (!window.confirm(`Delete category "${category.name}"?`)) return;

    try {
      await adminApi.deleteCategory(token, category.id);
      setCategories((current) => current.filter((entry) => entry.id !== category.id));
      showToast("Category deleted", "success");
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Could not delete category", "error");
    }
  };

  const createVenue = async () => {
    if (!token) return;
    if (!venueForm.name.trim() || !venueForm.city.trim() || !venueForm.address.trim()) {
      showToast("Venue name, city, and address are required", "error");
      return;
    }

    setIsSaving(true);
    try {
      const response = await adminApi.createVenue(token, {
        name: venueForm.name,
        city: venueForm.city,
        address: venueForm.address,
        capacity: parseCapacity(venueForm.capacity),
        notes: venueForm.notes,
      });
      setVenues((current) =>
        [...current, response.venue].sort((a, b) => `${a.city} ${a.name}`.localeCompare(`${b.city} ${b.name}`))
      );
      setVenueForm(emptyVenue);
      showToast("Venue added", "success");
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Could not add venue", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const saveVenue = async (venue: AdminVenue) => {
    if (!token) return;
    if (!editingVenue.name.trim() || !editingVenue.city.trim() || !editingVenue.address.trim()) {
      showToast("Venue name, city, and address are required", "error");
      return;
    }

    setIsSaving(true);
    try {
      const response = await adminApi.updateVenue(token, venue.id, {
        name: editingVenue.name,
        city: editingVenue.city,
        address: editingVenue.address,
        capacity: parseCapacity(editingVenue.capacity),
        notes: editingVenue.notes,
      });
      setVenues((current) => current.map((entry) => (entry.id === venue.id ? response.venue : entry)));
      setEditingVenueId(null);
      showToast("Venue updated", "success");
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Could not update venue", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleVenue = async (venue: AdminVenue) => {
    if (!token) return;
    try {
      const response = await adminApi.updateVenue(token, venue.id, { active: !venue.active });
      setVenues((current) => current.map((entry) => (entry.id === venue.id ? response.venue : entry)));
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Could not update venue", "error");
    }
  };

  const deleteVenue = async (venue: AdminVenue) => {
    if (!token) return;
    if (!window.confirm(`Delete venue "${venue.name}"?`)) return;

    try {
      await adminApi.deleteVenue(token, venue.id);
      setVenues((current) => current.filter((entry) => entry.id !== venue.id));
      showToast("Venue deleted", "success");
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Could not delete venue", "error");
    }
  };

  if (!canManage) {
    return (
      <div className="ego-surface px-6 py-12 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#fef2f2] text-[#dc2626]">
          <Tags size={20} />
        </div>
        <h2 className="text-lg font-bold text-foreground">Catalog setup is restricted</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Only super admins and roles with catalog management access can manage venues and categories.
        </p>
      </div>
    );
  }

  if (isLoading) return <LoadingState label="Loading venues and categories..." />;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <SummaryBox icon={Tags} label="Active categories" value={stats.activeCategories} />
        <SummaryBox icon={Tags} label="Inactive categories" value={stats.inactiveCategories} muted />
        <SummaryBox icon={Building2} label="Active venues" value={stats.activeVenues} />
        <SummaryBox icon={Building2} label="Inactive venues" value={stats.inactiveVenues} muted />
      </div>

      {error && (
        <div className="ego-surface border-l-4 border-l-red-500 px-5 py-4 text-sm font-semibold text-red-600">
          {error}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <section className="ego-surface overflow-hidden">
          <PanelHeader
            icon={Tags}
            title="Categories"
            subtitle="Create the labels customers and admins use to group screenings."
          />

          <div className="border-b border-line p-5">
            <div className="grid gap-3">
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-foreground">Name</span>
                <input
                  value={categoryForm.name}
                  onChange={(event) =>
                    setCategoryForm((current) => ({ ...current, name: event.target.value }))
                  }
                  className="ego-input"
                  placeholder="Film festival"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-semibold text-foreground">Description</span>
                <textarea
                  value={categoryForm.description}
                  onChange={(event) =>
                    setCategoryForm((current) => ({ ...current, description: event.target.value }))
                  }
                  rows={3}
                  className="ego-input resize-none"
                  placeholder="Optional internal description"
                />
              </label>
              <button type="button" onClick={createCategory} disabled={isSaving} className="ego-btn-primary">
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Add category
              </button>
            </div>
          </div>

          <div className="divide-y divide-line">
            {categories.length === 0 ? (
              <EmptyRow label="No categories yet" />
            ) : (
              categories.map((category) => {
                const isEditing = editingCategoryId === category.id;
                return (
                  <div key={category.id} className="p-5">
                    {isEditing ? (
                      <div className="grid gap-3">
                        <input
                          value={editingCategory.name}
                          onChange={(event) =>
                            setEditingCategory((current) => ({ ...current, name: event.target.value }))
                          }
                          className="ego-input"
                        />
                        <textarea
                          value={editingCategory.description}
                          onChange={(event) =>
                            setEditingCategory((current) => ({ ...current, description: event.target.value }))
                          }
                          rows={3}
                          className="ego-input resize-none"
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => void saveCategory(category)}
                            disabled={isSaving}
                            className="ego-btn-primary"
                          >
                            <Check size={15} /> Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingCategoryId(null)}
                            className="ego-btn-secondary"
                          >
                            <X size={15} /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-bold text-foreground">{category.name}</h3>
                            <span className={activeBadge(category.active)}>
                              {category.active ? "Active" : "Inactive"}
                            </span>
                          </div>
                          {category.description && (
                            <p className="mt-1 text-sm text-muted-foreground">{category.description}</p>
                          )}
                        </div>
                        <RowActions
                          active={category.active}
                          onToggle={() => void toggleCategory(category)}
                          onEdit={() => {
                            setEditingCategoryId(category.id);
                            setEditingCategory(categoryDraft(category));
                          }}
                          onDelete={() => void deleteCategory(category)}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="ego-surface overflow-hidden">
          <PanelHeader
            icon={Building2}
            title="Venues"
            subtitle="Save reusable screening locations so event forms stay consistent."
          />

          <div className="border-b border-line p-5">
            <div className="grid gap-3 md:grid-cols-2">
              <TextField
                label="Venue name"
                value={venueForm.name}
                onChange={(value) => setVenueForm((current) => ({ ...current, name: value }))}
                placeholder="Silverbird Accra Mall"
              />
              <TextField
                label="City"
                value={venueForm.city}
                onChange={(value) => setVenueForm((current) => ({ ...current, city: value }))}
                placeholder="Accra"
              />
              <label className="block md:col-span-2">
                <span className="mb-1.5 block text-sm font-semibold text-foreground">Address</span>
                <input
                  value={venueForm.address}
                  onChange={(event) =>
                    setVenueForm((current) => ({ ...current, address: event.target.value }))
                  }
                  className="ego-input"
                  placeholder="Accra Mall, Tetteh Quarshie Interchange"
                />
              </label>
              <TextField
                label="Capacity"
                value={venueForm.capacity}
                onChange={(value) => setVenueForm((current) => ({ ...current, capacity: value }))}
                placeholder="250"
                type="number"
              />
              <TextField
                label="Notes"
                value={venueForm.notes}
                onChange={(value) => setVenueForm((current) => ({ ...current, notes: value }))}
                placeholder="Main hall"
              />
              <button
                type="button"
                onClick={createVenue}
                disabled={isSaving}
                className="ego-btn-primary md:col-span-2"
              >
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Add venue
              </button>
            </div>
          </div>

          <div className="divide-y divide-line">
            {venues.length === 0 ? (
              <EmptyRow label="No venues yet" />
            ) : (
              venues.map((venue) => {
                const isEditing = editingVenueId === venue.id;
                return (
                  <div key={venue.id} className="p-5">
                    {isEditing ? (
                      <div className="grid gap-3 md:grid-cols-2">
                        <TextField
                          label="Venue name"
                          value={editingVenue.name}
                          onChange={(value) => setEditingVenue((current) => ({ ...current, name: value }))}
                        />
                        <TextField
                          label="City"
                          value={editingVenue.city}
                          onChange={(value) => setEditingVenue((current) => ({ ...current, city: value }))}
                        />
                        <label className="block md:col-span-2">
                          <span className="mb-1.5 block text-sm font-semibold text-foreground">Address</span>
                          <input
                            value={editingVenue.address}
                            onChange={(event) =>
                              setEditingVenue((current) => ({ ...current, address: event.target.value }))
                            }
                            className="ego-input"
                          />
                        </label>
                        <TextField
                          label="Capacity"
                          value={editingVenue.capacity}
                          onChange={(value) =>
                            setEditingVenue((current) => ({ ...current, capacity: value }))
                          }
                          type="number"
                        />
                        <TextField
                          label="Notes"
                          value={editingVenue.notes}
                          onChange={(value) => setEditingVenue((current) => ({ ...current, notes: value }))}
                        />
                        <div className="flex flex-wrap gap-2 md:col-span-2">
                          <button
                            type="button"
                            onClick={() => void saveVenue(venue)}
                            disabled={isSaving}
                            className="ego-btn-primary"
                          >
                            <Check size={15} /> Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingVenueId(null)}
                            className="ego-btn-secondary"
                          >
                            <X size={15} /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-bold text-foreground">{venue.name}</h3>
                            <span className={activeBadge(venue.active)}>
                              {venue.active ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              <MapPin size={14} /> {venue.city}
                            </span>
                            <span>{venue.address}</span>
                            {venue.capacity > 0 && <span>{venue.capacity.toLocaleString()} capacity</span>}
                          </div>
                          {venue.notes && <p className="mt-2 text-sm text-muted-foreground">{venue.notes}</p>}
                        </div>
                        <RowActions
                          active={venue.active}
                          onToggle={() => void toggleVenue(venue)}
                          onEdit={() => {
                            setEditingVenueId(venue.id);
                            setEditingVenue(venueDraft(venue));
                          }}
                          onDelete={() => void deleteVenue(venue)}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function SummaryBox({
  icon: Icon,
  label,
  value,
  muted = false,
}: {
  icon: typeof Tags;
  label: string;
  value: number;
  muted?: boolean;
}) {
  return (
    <div className="ego-surface p-5">
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-xl ${
          muted
            ? "bg-surface-muted text-muted-foreground"
            : "bg-[#fff1ea] text-[#c2410c] dark:bg-[rgba(245,158,11,0.14)]"
        }`}
      >
        <Icon size={18} />
      </div>
      <div className="mt-5 text-2xl font-bold tracking-tight text-foreground">{value}</div>
      <div className="mt-1 text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

function PanelHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: typeof Tags;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="border-b border-line px-5 py-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fff1ea] text-[#c2410c] dark:bg-[rgba(245,158,11,0.14)]">
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-foreground">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="ego-input"
      />
    </label>
  );
}

function RowActions({
  active,
  onToggle,
  onEdit,
  onDelete,
}: {
  active: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex shrink-0 flex-wrap gap-2">
      <button type="button" onClick={onToggle} className="ego-btn-secondary !py-2 !px-3 text-xs">
        {active ? "Deactivate" : "Activate"}
      </button>
      <button type="button" onClick={onEdit} className="ego-btn-secondary !py-2 !px-3 text-xs">
        <Edit3 size={14} /> Edit
      </button>
      <button type="button" onClick={onDelete} className="ego-btn-danger !py-2 !px-3 text-xs">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function EmptyRow({ label }: { label: string }) {
  return <div className="px-5 py-10 text-center text-sm text-muted-foreground">{label}</div>;
}
